import { FC } from 'react'
import { NavLink, useHistory } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Capacitor } from '@capacitor/core'
import { AxiosError } from 'axios'
import { OpenAPI } from 'services/Api'

import { Card as MerinioCard } from 'components/UI'
import { Alert, Card } from 'ui'
import { useTranslation } from 'react-i18next'

import { Button, Form, Input } from 'components/Forms'

type InitialValues = {
  email: string
}

const ForgotCompany: FC = () => {
  const history = useHistory()
  const { t } = useTranslation('legacy')
  const ForgotCompanySchema = Yup.object().shape({
    email: Yup.string().email('auth.MUST_BE_A_VALID_EMAIL').min(5, 'auth.TOO_SHORT').max(100, 'auth.TOO_LONG').required('auth.REQUIRED')
  })

  const companyInURL = window.location.hostname.split('.')[0]
  const invalidCompany = !['api'].includes(companyInURL)
  const initialValues: InitialValues = { email: '' }

  const onSubmit = async (values: InitialValues, actions: FormikHelpers<InitialValues>) => {
    const { email } = values
    try {
      const { message, success } = await OpenAPI.post('/forget/company', { email })
      actions.setSubmitting(false)
      if (!success) return actions.setStatus({ msg: `auth.${message}` })
      actions.resetForm({ values: { email: '' } })
      //Send back the user to the company page with a state that will allow to show a confirmation alert
      history.push({ pathname: '/company', state: { emailConfirmation: 'COMPANY' } })
    } catch (err) {
      const error = err as AxiosError
      actions.setSubmitting(false)

      actions.setStatus({ msg: error?.message })
    }
  }

  // Return the login form
  return (
    <MerinioCard
      title="auth.FORGOT_COMPANY"
      titleLink={
        <NavLink to="/company">
          <i className="fas fa-caret-left mr-2"></i>
          {t('auth.RETURN_COMPANY')}
        </NavLink>
      }
    >
      <Card.Body>
        <Formik validationSchema={ForgotCompanySchema} initialValues={initialValues} onSubmit={onSubmit}>
          {({ status, isSubmitting, dirty, resetForm, setStatus }) => (
            <Form resetForm={resetForm} dirty={!!dirty && !isSubmitting}>
              {({ leavingButton }) => (
                <>
                  <Alert onClose={() => setStatus()} show={!!status?.msg}>
                    {status?.msg}
                  </Alert>
                  {!Capacitor.isNativePlatform() && invalidCompany ? <Alert>{t('auth.NO_COMPANY_EXISTS_ON_URL', { co: companyInURL })}</Alert> : null}
                  <Input type="email" module="auth" name="email" autoCorrect="off" autoCapitalize="none" required />
                  <Button type="submit" isSubmitting={isSubmitting} text="auth.SUBMIT" />
                  {leavingButton}
                </>
              )}
            </Form>
          )}
        </Formik>
      </Card.Body>
    </MerinioCard>
  )
}

export default ForgotCompany
